import { INBOX_PATH, resolveNotificationTarget } from "./notificationTarget";

/**
 * What a tapped push opens. The payload is `{ notificationId, kind }` with NO
 * path — a single alert's destination is resolved by the server at the moment
 * it is opened, never frozen when it was sent — or `{ path, kind: "summary" }`
 * for a summary of several alerts, which opens the inbox and marks nothing
 * read.
 *
 * Import-free apart from the destination guard, so it stays a pure function of
 * the payload. Anything this binary cannot read opens the inbox, where the row
 * is visible: an older binary must never no-op on a newer payload.
 */
export type PushTap = { kind: "notification"; id: string } | { kind: "path"; path: string };

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const INBOX_TAP: PushTap = { kind: "path", path: INBOX_PATH };

/** `data` is the push's whole userInfo on iOS: our keys sit beside `aps`. */
export const resolvePushTap = (data: unknown): PushTap => {
  if (typeof data !== "object" || data === null) return INBOX_TAP;
  const { notificationId, path } = data as { notificationId?: unknown; path?: unknown };

  // An id always wins: that destination is the server's to resolve.
  if (typeof notificationId === "string" && UUID_PATTERN.test(notificationId)) {
    return { kind: "notification", id: notificationId };
  }

  // Push only exists in the native app, so the native billing block applies.
  if (typeof path === "string") {
    const resolved = resolveNotificationTarget(path, null, { isNative: true });
    if (resolved.kind === "internal") return { kind: "path", path: resolved.path };
  }
  return INBOX_TAP;
};

/**
 * The same tap as a route, for when nothing mounted can open it directly (a
 * cold start, or signed out): the /n/:id link route opens it exactly as a tap
 * would, and it survives sign-in as the remembered destination.
 */
export const pushTapPath = (tap: PushTap): string => {
  if (tap.kind === "notification") return `/n/${tap.id}?s=push`;
  return tap.path;
};
